import React, { useRef } from "react";

import classNames from "classnames";
import Image from "next/image";

import { StorefrontData, StoreFrontModals } from "data/storefront";
import { Button, Icon, IconVariant } from "studio-design-system";
import { useWindowSize } from "usehooks-ts";

import { CREATORS_PAGE_SHARE_MODAL_VISIT } from "~/analytics/Amplitude/amplitudeConstants";
import { addUTMPropertiesToEvent } from "~/analytics/Amplitude/amplitudeUtils";
import { useAnalytics } from "~/analytics/useAnalytics";
import { CreatorCheck } from "~/components/common/CreatorInfo/CreatorCheck";
import { ImageWithBlur } from "~/components/common/ImageWithBlur/ImageWithBlur";
import { HeartIcon } from "~/components/storefront/icons/HeartIcon";
import { ShareIcon } from "~/components/storefront/icons/ShareIcon";
import { isDescWrapping } from "~/util/stringUtils";

import * as styles from "./StorefrontHeroSection.css";

export const StorefrontHeroSection = ({
  storefrontData,
  setShowModal,
  isUserFollowingStorefront,
}: {
  storefrontData: StorefrontData;
  setShowModal: (modal: StoreFrontModals) => void;
  isUserFollowingStorefront: boolean;
}): React.ReactElement => {
  const { trackAmplitudeEvent } = useAnalytics();
  const { width } = useWindowSize();
  const isMobileScreen = width && width <= 630;

  const lineImg =
    "https://res.cloudinary.com/monthly/image/upload/v1674736005/monthly-admin-images/creator-dashboard/lines.png";
  const descriptionRef = useRef<any>(null);

  const isDescriptionWrapping = isDescWrapping(descriptionRef.current);

  const handleShare = () => {
    setShowModal("share");
    trackAmplitudeEvent(CREATORS_PAGE_SHARE_MODAL_VISIT, addUTMPropertiesToEvent());
  };

  return (
    <div className={styles.outerContainer}>
      <div className={styles.container}>
        <div className={styles.heroImgWrap}>
          <div className={styles.imageContainer}>
            <ImageWithBlur
              className={styles.heroImg}
              src={storefrontData.welcomePosterImageURL}
              layout={"fill"}
              alt={storefrontData.creatorName}
            />
          </div>
        </div>
        <div className={styles.heroBody}>
          <div className={styles.nameBox}>
            <div className={styles.mobileImg}>
              <ImageWithBlur
                src={storefrontData.welcomePosterImageURL}
                width={32}
                height={32}
                layout={"fixed"}
                objectFit={"cover"}
                alt={storefrontData.title}
              />
            </div>
            {/* {storefrontData.creatorName.trim() + "'S STUDIO"} */}
            {"Apprenticeship-Style Classes"}

            {/* <div className={styles.iconContainer}>
              <CreatorCheck />
            </div> */}
          </div>
          <div className={styles.signatureWrap}>
            <div className={styles.signatureImgWrap}>
              <ImageWithBlur
                src={lineImg}
                width={700}
                height={178}
                layout={"responsive"}
                alt={storefrontData.creatorName}
              />
            </div>
            <h1 className={styles.title}>{storefrontData.creatorName.trim() + "'s Studio"}</h1>
          </div>
          <div className={styles.descriptionWrap}>
            <p
              ref={descriptionRef}
              className={classNames(
                isDescriptionWrapping
                  ? classNames(styles.description, styles.pointerCursor)
                  : styles.description,
                isMobileScreen && styles.descriptionForMobile,
              )}
              onClick={() => isDescriptionWrapping && setShowModal("desc")}
            >
              {storefrontData.description}
            </p>
            {isDescriptionWrapping && (
              <span
                className={styles.descriptionMore}
                onClick={() => isDescriptionWrapping && setShowModal("desc")}
              >
                ...
                <span className={styles.descriptionMoreDeco}>More</span>
              </span>
            )}
          </div>
          <div className={styles.heroFooter}>
            {/* Commenting out for now until we have the intro videos
            <Button className={styles.heroFooterBtn} onClick={() => setShowModal("intro")}>
              <ShareIcon className={styles.btnIcon} />
              Watch {screen.width > 630 && "Intro"}
            </Button> */}
            {/* Commenting out for now until we have the share modal functionality working
            <Button className={styles.heroFooterBtn} onClick={() => setShowModal("share")}>
              <HeartIcon className={styles.btnIcon}/>
              Share
            </Button> */}
            <Button className={styles.heroFooterBtn} onClick={handleShare}>
              <ShareIcon className={styles.btnIcon} />
              Share
            </Button>
            {!isUserFollowingStorefront ? (
              <Button className={styles.heroFooterBtn} onClick={() => setShowModal("follow")}>
                <Icon
                  className={styles.btnIcon}
                  variant={IconVariant.HeartEmpty}
                  width={19}
                  height={19}
                />
                Follow
              </Button>
            ) : (
              <Button className={styles.heroFollowingBtn}>
                <Icon
                  className={styles.btnIcon}
                  variant={IconVariant.Checkmark}
                  width={19}
                  height={19}
                />
                Following
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
